import ScreenWrapper from "@/components/ScreenWrapper"
import { checkPermissions } from "@/utils/checkPermissions"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import {
  DataTable,
  createCustomColumn,
  createSortableColumn,
  getFullName,
  usePaginatedTableWithUrlState,
  createTableSearchSchema,
  TABLE_PAGE_SIZE,
  MultiLevelDropdownMenu,
  useExportMutation,
} from "toptaal-shared"
import type { ColumnDef } from "@tanstack/react-table"
import { TableHeaderActions } from "@/components/data-table/TableHeaderActions"
import { facturationMenuItems } from "@/constants/navigation"
import { invoiceableStudentQueryKeys } from "@/api/query-keys/invoiceableStudents"
import {
  useInvoiceableStudentFilters,
  INVOICEABLE_STUDENT_STORAGE_KEY,
} from "@/hooks/filters/useInvoiceableStudentFilters"
import {
  InvoiceableStudent,
  PAYMENT_SOURCE_LABEL,
} from "@/api/types/invoiceableStudents"
import { ChevronRightIcon } from "@heroicons/react/24/solid"

export const Route = createFileRoute("/facturatie/cursisten/")({
  component: RouteComponent,
  // TODO: check if "invoices" is still up to date
  beforeLoad: () => checkPermissions("read", "invoices"),
  validateSearch: createTableSearchSchema({
    defaultSortField: "id",
    defaultSortDirection: "asc",
  }),
})

const ALL_COLUMN_IDS = [
  "id",
  "name",
  "lastInvoicedLesson",
  "invoicedLessons",
  "lessonsToInvoice",
  "lessonMaterial",
  "paymentSource",
]

function RouteComponent() {
  const searchParams = useSearch({ from: "/facturatie/cursisten/" })
  const navigate = useNavigate({ from: "/facturatie/cursisten/" })

  const { filterItems, activeFilters, queryFn } = useInvoiceableStudentFilters({
    onPageReset: () =>
      navigate({ search: (prev) => ({ ...prev, page: 1 }), replace: true }),
  })

  const {
    isFetching,
    data,
    search,
    setSearch,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    pagination,
    columnVisibility,
    setColumnVisibility,
    resetColumnVisibility,
    mergedFilters,
    selectedColumns,
  } = usePaginatedTableWithUrlState({
    queryKey: [...invoiceableStudentQueryKeys.all, activeFilters],
    queryFn,
    pageSize: TABLE_PAGE_SIZE,
    searchParams,
    navigate,
    storageKey: INVOICEABLE_STUDENT_STORAGE_KEY,
    allColumnIds: ALL_COLUMN_IDS,
  })

  const handleGenerateConcept = (student: InvoiceableStudent) => {
    navigate({
      to: "/facturatie/cursisten/$studentId/factuur-genereren",
      params: { studentId: String(student.id) },
      search: {
        spoId: student.studentProjectOrganizationId,
        studentName: getFullName({
          firstName: student.firstName,
          lastName: student.lastName,
        }),
        paymentSource: student.paymentSource,
      },
    })
  }

  const columns: ColumnDef<InvoiceableStudent>[] = [
    createSortableColumn({ accessorKey: "id", name: "ID" }),
    createCustomColumn({
      id: "name",
      name: "Naam",
      cell: ({ row }: { row: { original: InvoiceableStudent } }) =>
        getFullName({
          firstName: row.original.firstName,
          lastName: row.original.lastName,
        }),
      accessorKey: "firstName",
    }),
    createCustomColumn({
      id: "lastInvoicedLesson",
      name: "Laatst gefact. les",
      cell: ({ row }: { row: { original: InvoiceableStudent } }) =>
        row.original.lastInvoicedLessonDate ?? "-",
      accessorKey: "lastInvoicedLessonDate",
      enableSorting: false,
    }),
    createCustomColumn({
      id: "invoicedLessons",
      name: "Gefact. lessen",
      cell: ({ row }: { row: { original: InvoiceableStudent } }) =>
        `${row.original.invoicedLessonsCount}`,
      accessorKey: "invoicedLessonsCount",
      enableSorting: false,
    }),
    createCustomColumn({
      id: "lessonsToInvoice",
      name: "Te fact. lessen",
      cell: ({ row }: { row: { original: InvoiceableStudent } }) =>
        `${row.original.invoiceableLessonsCount}`,
      accessorKey: "invoiceableLessonsCount",
      enableSorting: false,
    }),
    createCustomColumn({
      id: "lessonMaterial",
      name: "Les materiaal",
      cell: () => "-",
      accessorKey: "lessonMaterial",
      enableSorting: false,
    }),
    createCustomColumn({
      id: "paymentSource",
      name: "Soort",
      cell: ({ row }: { row: { original: InvoiceableStudent } }) =>
        PAYMENT_SOURCE_LABEL[row.original.paymentSource] ??
        row.original.paymentSource,
      accessorKey: "paymentSource",
    }),
    createCustomColumn({
      id: "generateConcept",
      name: "",
      cell: () => (
        <span className="font-bold text-primary-green">
          Concept genereren
          <ChevronRightIcon className="size-5 inline-block ml-1" />
        </span>
      ),
      accessorKey: "generateConcept",
      enableSorting: false,
    }),
  ]

  const { mutate: exportStudents } = useExportMutation({
    exportApiPrefix: "invoicing/students",
    filters: mergedFilters,
    selectedColumns,
    activeFilters,
  })

  return (
    <ScreenWrapper
      showManagementMenu
      managementMenuItemsFromProps={facturationMenuItems}
    >
      <DataTable
        title="Cursisten"
        data={data?.data.students || []}
        isLoading={isFetching}
        pagination={pagination}
        sortDirection={sortDirection}
        sortField={sortField}
        search={search}
        setSearch={setSearch}
        setSortField={setSortField}
        setSortDirection={setSortDirection}
        columns={columns}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        resetColumnVisibility={resetColumnVisibility}
        onRowClick={(_, rowData) => {
          handleGenerateConcept(rowData)
        }}
        headerLeftActions={<MultiLevelDropdownMenu items={filterItems} />}
        headerRightActions={
          <TableHeaderActions
            exportAction={{
              onClick: exportStudents,
            }}
          />
        }
      />
    </ScreenWrapper>
  )
}
